import { useMemo } from 'react';

import type { SkillFile, SkillFileContent } from '../../lib/api-client';
import { ResizableListPane } from '../../shell/ResizableListPane';

import { SkillFileViewer } from './SkillFileViewer';
import { buildTree, SkillTreeNode } from './SkillTreeNode';

interface Props {
  files: SkillFile[];
  filesStatus: 'idle' | 'loading' | 'success' | 'error';
  filesError?: string | undefined;
  selectedPath: string | null;
  onSelectFile: (path: string) => void;
  fileContent: SkillFileContent | null;
  fileStatus: 'idle' | 'loading' | 'success' | 'error';
  fileError?: string | undefined;
  /** True when the user is viewing a non-HEAD revision; switches viewer to diff mode. */
  isAtRevision: boolean;
  /** Label for the synthetic root folder in the file tree (e.g. the skill or agent name). */
  rootLabel: string;
}

const MAX_HEIGHT = '70vh';

export function SkillFileBrowser({
  files,
  filesStatus,
  filesError,
  selectedPath,
  onSelectFile,
  fileContent,
  fileStatus,
  fileError,
  isAtRevision,
  rootLabel,
}: Props) {
  const tree = useMemo(() => buildTree(files, rootLabel), [files, rootLabel]);

  return (
    <div
      style={{
        display: 'flex',
        height: MAX_HEIGHT,
        maxHeight: MAX_HEIGHT,
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
      }}
    >
      {/* Tree (left) — resizable; width persists per-view. */}
      <ResizableListPane
        storageKey="skill-files"
        defaultWidth={260}
        minWidth={180}
        maxWidth={480}
        paneStyle={{
          borderRight: '1px solid var(--border)',
          overflowY: 'auto',
          background: 'var(--bg-alt)',
          padding: '8px 0',
        }}
      >
        {filesStatus === 'loading' && (
          <div style={{ padding: 12, fontSize: 12, color: 'var(--text-mute)' }}>Loading files…</div>
        )}
        {filesStatus === 'error' && (
          <div style={{ padding: 12, fontSize: 12, color: 'var(--kind-failure)' }}>
            {filesError ?? 'Failed to load files.'}
          </div>
        )}
        {filesStatus === 'success' && tree.length === 0 && (
          <div style={{ padding: 12, fontSize: 12, color: 'var(--text-mute)' }}>
            No files in this skill.
          </div>
        )}
        {tree.map((node) => (
          <SkillTreeNode
            key={node.path}
            node={node}
            depth={0}
            selectedPath={selectedPath}
            onSelectFile={onSelectFile}
          />
        ))}
      </ResizableListPane>

      {/* File content (right) */}
      <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
        <SkillFileViewer
          content={fileContent}
          status={fileStatus}
          error={fileError}
          showAsDiff={isAtRevision}
        />
      </div>
    </div>
  );
}
